import {
  useEffect,
  useMemo,
  useRef,
  useState,
  ComponentType,
  ReactNode,
} from "react";
import { DayPicker } from "react-day-picker";

import SortNeutralIcon from "../shared-components/icons/SortNeutral";
import SortAscendingIcon from "../shared-components/icons/SortAscending";
import SortDescendingIcon from "../shared-components/icons/SortDescending";
import { FilterIcon, SearchIcon } from "../shared-components/icons";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { Modal } from "../shared-components/Modal";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import { useLocation } from "react-router-dom";
import { cacheUpdateEvents } from "../services/websocket/cacheUpdateEvents";
import { useFamaFlags } from "../api/FamaFlags";
import LoadingSpinner from "../shared-components/LoadingSpinner";

export type GenericObject = {
  [key: string]: any;
};

/** Stable empty array reference for "no data" to avoid effect loops. */
const EMPTY_DATA: GenericObject[] = [];

export type HeaderField = {
  display: string;
  key_field?: string;
  value_field: string;
  filterable: boolean;
  sortable: boolean;
  datePick?: boolean;
  searchable?: boolean;
  preFilter?: any[];
  filterVariant?: "listTriState";
  hideable?: boolean;
  renderFilterOption?: (value: string) => ReactNode;
  loadFilterOptions?: (search: string) => Promise<GenericObject[]>;
  /** Debounce for 3+ characters. 1–2 characters wait twice as long. Empty input fetches immediately. */
  filterOptionsDebounceMs?: number;
  /** When true, filter choices sort numerically ascending. Auto-detected when all values are numeric. */
  numeric?: boolean;
};

const SHORT_FILTER_QUERY_MAX_LENGTH = 2;

function getFilterOptionsDebounceMs(
  search: string,
  debounceMs: number
): number {
  if (!search) return 0;
  if (search.length <= SHORT_FILTER_QUERY_MAX_LENGTH) return debounceMs * 2;
  return debounceMs;
}

function numericSortKey(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isNaN(value) ? null : value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return null;
    const leadingNumber = trimmed.match(/^-?\d+(\.\d+)?/);
    if (leadingNumber) return Number(leadingNumber[0]);
    if (!Number.isNaN(Number(trimmed))) return Number(trimmed);
  }
  return null;
}

function isNumericFilterColumn(
  header: HeaderField,
  values: GenericObject[]
): boolean {
  if (header.numeric) return true;
  if (values.length === 0) return false;
  const field = header.value_field;
  return values.every((row) => numericSortKey(row[field]) !== null);
}

function compareFilterDistinctValues(
  a: GenericObject,
  b: GenericObject,
  header: HeaderField,
  isNumeric: boolean
): number {
  const field = header.value_field;
  const aVal = a[field];
  const bVal = b[field];

  if (isNumeric) {
    return numericSortKey(aVal)! - numericSortKey(bVal)!;
  }

  const aText = String(aVal ?? "");
  const bText = String(bVal ?? "");
  const caseInsensitiveOrder = aText.localeCompare(bText, undefined, {
    sensitivity: "base",
    numeric: true,
  });
  if (caseInsensitiveOrder !== 0) {
    return caseInsensitiveOrder;
  }
  return aText.localeCompare(bText);
}

function sortFilterDistinctValues(
  values: GenericObject[],
  header: HeaderField
): GenericObject[] {
  const isNumeric = isNumericFilterColumn(header, values);
  return [...values].sort((a, b) =>
    compareFilterDistinctValues(a, b, header, isNumeric)
  );
}

export type FilterValues = {
  field: string;
  values: any[];
};

export type TriStateFilterValue = {
  value: string;
  state: "include" | "exclude";
};

type FilterSortHeaderItemProps = {
  header: HeaderField;
  data: GenericObject[];
  sortField: string;
  reverse: boolean;
  activeFilters: FilterValues[];
  sortCallback: (fieldName: string) => void;
  setFilterCallback: (field: string, values: any[]) => void;
  trailingContent?: ReactNode;
};
const FilterSortHeaderItem = ({
  header,
  data,
  sortField,
  reverse,
  activeFilters,
  sortCallback,
  setFilterCallback,
  trailingContent,
}: FilterSortHeaderItemProps) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [distinctValues, setDistinctValues] = useState<GenericObject[]>([]);
  const [selectedValues, setSelectedValues] = useState<GenericObject[]>([]);
  const [triStateValues, setTriStateValues] = useState<
    Record<string, TriStateFilterValue["state"]>
  >({});
  const [search, setSearch] = useState<string>("");
  const [filterOptionsLoading, setFilterOptionsLoading] =
    useState<boolean>(false);
  const filterOptionsRequest = useRef<number>(0);
  const emptyFilterOptionsCache = useRef<GenericObject[] | null>(null);
  const loadFilterOptions = header.loadFilterOptions;
  const filterOptionsDebounceMs = header.filterOptionsDebounceMs;

  useEffect(() => {
    if (
      !data ||
      !header ||
      !header.filterable ||
      header.datePick ||
      header.loadFilterOptions
    )
      return;
    const newValues: GenericObject[] = [];
    if (header.filterVariant === "listTriState") {
      const values: string[] = [];
      for (const row of data) {
        const rowValues = Array.isArray(row[header.value_field])
          ? row[header.value_field]
          : [];
        for (const value of rowValues) {
          if (!values.includes(value)) {
            values.push(value);
            newValues.push({ [header.value_field]: value });
          }
        }
      }
    } else if (header.key_field) {
      // complex version with two columns for key and value
      const keys: string[] = [];
      for (const row of data) {
        const key = row[header.key_field];
        if (!keys.includes(key)) {
          keys.push(key);
          const newObj: GenericObject = {};
          newObj[header.key_field] = row[header.key_field];
          newObj[header.value_field] = row[header.value_field];
          newValues.push(newObj);
        }
      }
    } else {
      // simple version with one column for value
      const values: string[] = [];
      for (const row of data) {
        const value = row[header.value_field];
        if (!values.includes(value)) {
          values.push(value);
          const newObj: GenericObject = {};
          newObj[header.value_field] = row[header.value_field];
          newValues.push(newObj);
        }
      }
    }
    setDistinctValues(sortFilterDistinctValues(newValues, header));
  }, [data, header, setDistinctValues]);

  useEffect(() => {
    if (
      !modalOpen ||
      !header.filterable ||
      header.datePick ||
      !loadFilterOptions
    ) {
      if (!modalOpen) {
        filterOptionsRequest.current += 1;
        emptyFilterOptionsCache.current = null;
      }
      return;
    }

    if (!search && emptyFilterOptionsCache.current) {
      filterOptionsRequest.current += 1;
      setDistinctValues(emptyFilterOptionsCache.current);
      setFilterOptionsLoading(false);
      return;
    }

    const requestNumber = ++filterOptionsRequest.current;
    const delay = getFilterOptionsDebounceMs(
      search,
      filterOptionsDebounceMs ?? 1000
    );
    setDistinctValues([]);
    setFilterOptionsLoading(true);

    const timeout = window.setTimeout(async () => {
      try {
        const values = await loadFilterOptions(search);
        if (filterOptionsRequest.current === requestNumber) {
          if (!search) {
            emptyFilterOptionsCache.current = values;
          }
          setDistinctValues(values);
        }
      } catch {
        if (filterOptionsRequest.current === requestNumber) {
          setDistinctValues([]);
        }
      } finally {
        if (filterOptionsRequest.current === requestNumber) {
          setFilterOptionsLoading(false);
        }
      }
    }, delay);

    return () => window.clearTimeout(timeout);
  }, [
    filterOptionsDebounceMs,
    header.datePick,
    header.filterable,
    loadFilterOptions,
    modalOpen,
    search,
  ]);

  useEffect(() => {
    if (!header || !header.filterable) return;
    const activeFilter = (activeFilters || []).filter(
      (a) => a.field === (header.key_field || header.value_field)
    );
    if (activeFilter.length > 0) {
      const field = header.key_field || header.value_field;
      if (header.filterVariant === "listTriState") {
        const nextTriStateValues: Record<string, TriStateFilterValue["state"]> =
          {};
        for (const value of activeFilter[0].values as TriStateFilterValue[]) {
          nextTriStateValues[value.value] = value.state;
        }
        setTriStateValues(nextTriStateValues);
      } else if (header.datePick) {
        setSelectedValues([{ [field]: activeFilter[0].values }]);
      } else {
        const temp: GenericObject[] = [];
        for (const obj of distinctValues) {
          if (activeFilter[0].values.includes(obj[field])) {
            temp.push(obj);
          }
        }
        setSelectedValues(temp);
      }
    } else {
      setSelectedValues([]);
      setTriStateValues({});
    }
  }, [header, activeFilters, distinctValues, setSelectedValues]);

  function matchKeyValue(a: GenericObject, b: GenericObject) {
    return (
      a[header.key_field || header.value_field] ===
      b[header.key_field || header.value_field]
    );
  }

  function pickedDate(value: Date | undefined): void {
    const field = header.key_field || header.value_field;
    if (!value) {
      setFilterCallback(field, []);
    } else {
      setFilterCallback(field, [value]);
    }
  }

  function updateSelection(checked: boolean, obj: GenericObject): void {
    const selection = selectedValues.filter((a) => !matchKeyValue(a, obj));
    if (checked) {
      selection.push(obj);
    }
    const selected: string[] = [];
    for (const o of selection) {
      selected.push(o[header.key_field || header.value_field]);
    }
    setFilterCallback(header.key_field || header.value_field, selected);
  }

  function updateTriStateSelection(value: string): void {
    const currentState = triStateValues[value];
    const nextTriStateValues = { ...triStateValues };
    if (!currentState) {
      nextTriStateValues[value] = "include";
    } else if (currentState === "include") {
      nextTriStateValues[value] = "exclude";
    } else {
      delete nextTriStateValues[value];
    }
    setTriStateValues(nextTriStateValues);
    setFilterCallback(
      header.value_field,
      Object.entries(nextTriStateValues).map(([filterValue, state]) => ({
        value: filterValue,
        state,
      }))
    );
  }

  function triStateIndicator(value: string): JSX.Element {
    const state = triStateValues[value];
    if (state === "include") {
      return (
        <span className="inline-flex h-4 w-4 items-center justify-center rounded border border-green-700 bg-green-600 text-[10px] font-bold text-white">
          <svg viewBox="0 0 16 16" className="h-3 w-3" aria-hidden="true">
            <path
              d="M3.5 8.5L6.5 11.5L12.5 4.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      );
    }
    if (state === "exclude") {
      return (
        <span className="inline-flex h-4 w-4 items-center justify-center rounded border border-gray-800 bg-white text-[10px] font-bold text-red-600">
          X
        </span>
      );
    }
    return (
      <span className="inline-flex h-4 w-4 rounded border border-gray-500 bg-gray-300" />
    );
  }

  function matchesSearch(value: unknown): boolean {
    return String(value ?? "")
      .toLowerCase()
      .includes(search);
  }

  function applySearch(): void {
    const field = header.key_field || header.value_field;
    setFilterCallback(field, search ? [search] : []);
    setModalOpen(false);
  }

  const visibleFilterOptions = distinctValues.filter(
    (v) => loadFilterOptions || matchesSearch(v[header.value_field])
  );

  return (
    <div key={header.value_field}>
      <div
        onClick={() => sortCallback(header.value_field)}
        className="flex gap-2 items-center"
      >
        <div className="text-md font-bold text-gray-900">{header.display}</div>
        {header.sortable && (
          <div>
            {sortField === header.value_field ? (
              reverse ? (
                <SortDescendingIcon className="w-4" />
              ) : (
                <SortAscendingIcon className="w-4" />
              )
            ) : (
              <SortNeutralIcon className="w-4" />
            )}
          </div>
        )}
        {header.filterable && (
          <div>
            {(activeFilters || []).filter(
              (a) => a.field === (header.key_field || header.value_field)
            ).length > 0 ? (
              <FilterIcon
                aria-label={`Filter ${header.display}`}
                className="w-5"
                stroke="green"
                fill="green"
                onClick={(e) => {
                  e.stopPropagation();
                  setModalOpen(!modalOpen);
                }}
              />
            ) : (
              <FilterIcon
                aria-label={`Filter ${header.display}`}
                className="w-5"
                onClick={(e) => {
                  e.stopPropagation();
                  setModalOpen(!modalOpen);
                }}
              />
            )}
          </div>
        )}
        {trailingContent}
      </div>
      <Modal
        open={modalOpen}
        width="w-[600px]"
        onClose={() => setModalOpen(false)}
      >
        {header.datePick ? (
          <div className="flex flex-col gap-2">
            <div className="text-lg font-semibold">{header.display}</div>
            <div className="flex flex-row gap-2">
              <DayPicker
                mode="single"
                selected={
                  selectedValues.length > 0
                    ? new Date(
                        Date.parse(
                          selectedValues[0][
                            header.key_field || header.value_field
                          ]
                        )
                      )
                    : undefined
                }
                onSelect={(e) => pickedDate(e)}
                defaultMonth={
                  selectedValues.length > 0
                    ? new Date(
                        Date.parse(
                          selectedValues[0][
                            header.key_field || header.value_field
                          ]
                        )
                      )
                    : new Date()
                }
              />
              <Button
                text="Clear"
                type={ButtonTypes.removeX}
                colorType={ButtonColorTypes.cancel}
                onClick={(e) => {
                  setFilterCallback(header.key_field || header.value_field, []);
                  setSearch("");
                  setModalOpen(false);
                  e.preventDefault();
                }}
                className="w-24 h-8"
              />
            </div>
            <div className="text-right">
              <Button
                text="Apply"
                type={ButtonTypes.default}
                colorType={ButtonColorTypes.primary}
                onClick={(e) => {
                  setModalOpen(false);
                  e.preventDefault();
                }}
                className="w-24"
              />
            </div>
          </div>
        ) : header.filterVariant === "listTriState" ? (
          <div className="flex flex-col gap-2">
            <div className="text-lg font-semibold">{header.display}</div>
            <div className="flex gap-2 w-full">
              <div className="w-full relative">
                <input
                  type="text"
                  className="bg-white rounded-md border border-gray-200 w-full px-3 py-2 text-sm"
                  value={search}
                  onChange={(e) => setSearch(e.target.value?.toLowerCase())}
                />
                <SearchIcon className="w-5 h-5 absolute right-2 top-2 text-gray-300" />
              </div>
              <Button
                text="Clear"
                type={ButtonTypes.removeX}
                colorType={ButtonColorTypes.cancel}
                onClick={(e) => {
                  setFilterCallback(header.value_field, []);
                  setSearch("");
                  setModalOpen(false);
                  e.preventDefault();
                }}
                className="w-24"
              />
            </div>
            <div className="text-xs text-gray-500">
              Gray = either, green check = must include, red X = must exclude.
            </div>
            <div className="bg-white rounded-md border border-gray-200 w-full px-3 py-2 max-h-1/2 overflow-y-auto">
              {distinctValues
                .filter((v) => matchesSearch(v[header.value_field]))
                .map((value, i) => {
                  const filterValue = value[header.value_field];
                  return (
                    <button
                      key={i}
                      type="button"
                      className="flex items-center gap-2 text-left"
                      onClick={() => updateTriStateSelection(filterValue)}
                    >
                      {triStateIndicator(filterValue)}
                      {header.renderFilterOption?.(filterValue)}
                      <span>{filterValue}</span>
                    </button>
                  );
                })}
            </div>
            <div className="text-right">
              <Button
                text="Apply"
                type={ButtonTypes.default}
                colorType={ButtonColorTypes.primary}
                onClick={(e) => {
                  setModalOpen(false);
                  e.preventDefault();
                }}
                className="w-24"
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="text-lg font-semibold">{header.display}</div>
            <div className="flex gap-2 w-full">
              <div className="w-full relative">
                <input
                  type="text"
                  className="bg-white rounded-md border border-gray-200 w-full px-3 py-2 text-sm"
                  value={search}
                  onChange={(e) => setSearch(e.target.value?.toLowerCase())}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      header.searchable &&
                      loadFilterOptions
                    ) {
                      applySearch();
                      e.preventDefault();
                    }
                  }}
                />
                <SearchIcon className="w-5 h-5 absolute right-2 top-2 text-gray-300" />
              </div>
              <Button
                text="Clear"
                type={ButtonTypes.removeX}
                colorType={ButtonColorTypes.cancel}
                onClick={(e) => {
                  setFilterCallback(header.key_field || header.value_field, []);
                  setSearch("");
                  setModalOpen(false);
                  e.preventDefault();
                }}
                className="w-24"
              />
            </div>
            <div className="bg-white rounded-md border border-gray-200 w-full px-3 py-2 max-h-1/2 overflow-y-auto">
              {filterOptionsLoading ? (
                <LoadingSpinner className="py-2" />
              ) : loadFilterOptions && visibleFilterOptions.length === 0 ? (
                <div className="text-gray-500">no data found</div>
              ) : (
                visibleFilterOptions.map((value, i) => (
                  <div key={i} className="flex gap-2">
                    <input
                      type="checkbox"
                      id={value[header.key_field || header.value_field]}
                      checked={selectedValues.some((sv) =>
                        matchKeyValue(sv, value)
                      )}
                      onChange={(e) => updateSelection(e.target.checked, value)}
                    />
                    <label
                      htmlFor={value[header.key_field || header.value_field]}
                    >
                      {value[header.value_field]}
                    </label>
                  </div>
                ))
              )}
            </div>
            <div className="text-right">
              <Button
                text="Apply"
                type={ButtonTypes.default}
                colorType={ButtonColorTypes.primary}
                onClick={(e) => {
                  setModalOpen(false);
                  if (
                    header.searchable &&
                    search &&
                    selectedValues.length === 0
                  ) {
                    applySearch();
                  }
                  e.preventDefault();
                }}
                className="w-24"
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

type FilterSortHeaderProps = {
  headers: HeaderField[];
  sortField: string;
  reverse: boolean;
  sortCallback: (fieldName: string) => void;
  data: GenericObject[];
  activeFilters: FilterValues[];
  setFilterCallback: (field: string, values: any[]) => void;
  overrideDisplay?: ReactNode;
  hidden?: boolean;
  onHiddenChange?: (hidden: boolean) => void;
  headerAccessory?: ReactNode;
};
export const FilterSortHeader = ({
  headers,
  sortField,
  reverse,
  sortCallback,
  data,
  activeFilters,
  setFilterCallback,
  overrideDisplay,
  hidden = false,
  onHiddenChange,
  headerAccessory,
}: FilterSortHeaderProps) => {
  const isHideable = headers.some((header) => header.hideable);
  const hiddenDisplay = headers[0]?.display.slice(0, 4) ?? "";
  const visibilityToggle = isHideable ? (
    <button
      aria-label={hidden ? "Show column" : "Hide column"}
      className="flex text-gray-400 hover:text-gray-600"
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onHiddenChange?.(!hidden);
      }}
    >
      {hidden ? (
        <EyeSlashIcon className="h-4 w-4" />
      ) : (
        <EyeIcon className="h-4 w-4" />
      )}
    </button>
  ) : null;

  if (hidden && isHideable) {
    return (
      <div className="flex h-full w-full items-center justify-center gap-1">
        <div
          className="text-center text-[10px] font-bold uppercase text-gray-700"
          title={headers.map((header) => header.display).join(" / ")}
        >
          {hiddenDisplay}
        </div>
        {visibilityToggle}
      </div>
    );
  }

  return overrideDisplay ? (
    <div className="flex flex-1 items-center gap-2">
      {overrideDisplay}
      {visibilityToggle}
    </div>
  ) : (
    <div className="flex-1">
      <div className="inline-block">
        {headers.map((header, index) => (
          <FilterSortHeaderItem
            key={header.value_field}
            header={header}
            data={data}
            sortField={sortField}
            reverse={reverse}
            sortCallback={sortCallback}
            activeFilters={activeFilters}
            setFilterCallback={setFilterCallback}
            trailingContent={
              index === 0 ? (
                <>
                  {headerAccessory}
                  {visibilityToggle}
                </>
              ) : undefined
            }
          />
        ))}
      </div>
    </div>
  );
};

interface LoaderFunction {
  data?: GenericObject[];
  mutate: (force: boolean) => void;
  isLoading: boolean;
}

export interface WithFilterSortHeaderBaseProps {
  page_size: number;
  date_fields: string[];
  loader: LoaderFunction;
  header_definitions: HeaderField[][];
  /** Initial column and fallback when user clears sort (`sortField` empty). */
  default_sort_field?: string;
  default_sort_reverse?: boolean;
}

export interface WithFilterSortHeaderInjectedProps {
  fullData?: GenericObject[];
  fullDataLoading: boolean;
  filteredData: GenericObject[];
  activeData: GenericObject[];
  page: number;
  setPage: (page: number) => void;
  sortField: string;
  reverse: boolean;
  setSortBy: (field: string) => void;
  activeFilters: FilterValues[];
  setFilters: (field: string, values: any[]) => void;
  clearFilters: () => void;
  forceReload: () => void;
}

export function WithFilterSortHeader<P extends WithFilterSortHeaderBaseProps>(
  WrappedComponent: ComponentType<P & WithFilterSortHeaderInjectedProps>
) {
  const WrappedComponentEx = (props: P) => {
    const location = useLocation();
    const force = (location.state as any)?.force || false;
    const { v3UiReactsToWebsocketWorkflowEvents: liveUpdates } = useFamaFlags();
    const [filteredData, setFilteredData] = useState<GenericObject[]>([]);
    const [activeData, setActiveData] = useState<GenericObject[]>([]);
    const [page, setPage] = useState<number>(1);
    const [sortField, setSortField] = useState<string>(
      () => props.default_sort_field ?? ""
    );
    const [reverse, setReverse] = useState<boolean>(
      () => props.default_sort_reverse ?? false
    );

    function setSortBy(field: string) {
      setPage(1);
      if (field === sortField) {
        if (reverse) {
          setSortField("");
        }
        setReverse(!reverse);
      } else {
        setSortField(field);
        setReverse(false);
      }
    }

    const [activeFilters, setActiveFilters] = useState<FilterValues[]>([]);
    function setFilters(field: string, values: any[]) {
      const newFilters: FilterValues[] = activeFilters.filter(
        (a) => a.field !== field
      );
      if (values.length > 0) {
        newFilters.push({
          field: field,
          values: values,
        });
      }
      setActiveFilters(newFilters);
    }
    function clearFilters() {
      setActiveFilters([]);
    }

    const {
      data: fullData,
      mutate: fullDataGet,
      isLoading: fullDataLoading,
    } = props.loader;

    const fullDataRef = useRef(fullData);
    fullDataRef.current = fullData;

    useEffect(() => {
      fullDataGet(force);
    }, [fullDataGet, force]);

    function forceReload() {
      fullDataGet(true);
    }

    useEffect(() => {
      const unsubscribe = cacheUpdateEvents.subscribe(
        (affectedReportUuids, meta) => {
          // Lock/unlock events update the Assigned To cell in place; skip the refetch.
          if (liveUpdates && meta.reload360Lists === false) return;
          const currentFullData = fullDataRef.current;
          if (!currentFullData?.length || affectedReportUuids.length === 0) {
            return;
          }
          const shouldReload = affectedReportUuids.some((uuid) =>
            currentFullData.some((row) => row.request_uuid === uuid)
          );
          if (shouldReload) {
            fullDataGet(true);
          }
        }
      );
      return unsubscribe;
    }, [fullDataGet, liveUpdates]);

    const preFilteredData = useMemo(() => {
      if (!fullData) return undefined;
      let data = fullData;
      for (const headerGroup of props.header_definitions) {
        for (const header of headerGroup) {
          const preFilter = header.preFilter;
          if (preFilter === undefined) continue;
          if (preFilter.length === 0) {
            return [];
          }
          const field = header.key_field ?? header.value_field;
          data = data.filter((row) => preFilter.includes(row[field]));
        }
      }
      return data;
    }, [fullData, props.header_definitions]);

    useEffect(() => {
      if (!preFilteredData) return;
      let temp = [...preFilteredData];
      for (const activeFilter of activeFilters) {
        const field = activeFilter.field;
        const header = props.header_definitions
          .flat()
          .find(
            (headerDef) =>
              (headerDef.key_field ?? headerDef.value_field) === field
          );
        if (header?.filterVariant === "listTriState") {
          const triStateValues = activeFilter.values as TriStateFilterValue[];
          const included = triStateValues
            .filter((value) => value.state === "include")
            .map((value) => value.value);
          const excluded = triStateValues
            .filter((value) => value.state === "exclude")
            .map((value) => value.value);
          temp = temp.filter((row) => {
            const rowValues = Array.isArray(row[field]) ? row[field] : [];
            return (
              included.every((value) => rowValues.includes(value)) &&
              excluded.every((value) => !rowValues.includes(value))
            );
          });
        } else {
          temp = temp.filter((row) => activeFilter.values.includes(row[field]));
        }
      }
      setFilteredData(temp);
    }, [
      preFilteredData,
      activeFilters,
      props.header_definitions,
      setFilteredData,
    ]);

    useEffect(() => {
      let temp = [...filteredData];
      const defaultField = props.default_sort_field ?? "";
      const defaultRev = props.default_sort_reverse ?? false;
      const effectiveField = sortField || defaultField;
      const desc =
        sortField === "" && defaultField !== "" ? defaultRev : reverse;
      function getSorter(
        field: string
      ): (a: GenericObject, b: GenericObject) => number {
        if (field === "") {
          return (_a, _b) => {
            return 0;
          };
        }
        if (props.date_fields.includes(field)) {
          return (a: GenericObject, b: GenericObject): number => {
            return Date.parse(a[field]) - Date.parse(b[field]);
          };
        }
        return (a: GenericObject, b: GenericObject): number => {
          return a[field].localeCompare(b[field]);
        };
      }
      temp.sort(getSorter(effectiveField));
      if (desc) {
        temp.reverse();
      }
      const start = (page - 1) * props.page_size;
      const end = start + props.page_size;
      setActiveData(temp.slice(start, end));
    }, [
      props.date_fields,
      props.page_size,
      props.default_sort_field,
      props.default_sort_reverse,
      filteredData,
      page,
      sortField,
      reverse,
      setActiveData,
    ]);
    const loadingOrPreparingData =
      fullDataLoading || typeof fullData === "undefined";

    return (
      <WrappedComponent
        {...(props as P)}
        fullData={preFilteredData ?? EMPTY_DATA}
        fullDataLoading={loadingOrPreparingData}
        filteredData={filteredData}
        activeData={activeData}
        page={page}
        setPage={setPage}
        sortField={sortField}
        reverse={reverse}
        activeFilters={activeFilters}
        setSortBy={setSortBy}
        setFilters={setFilters}
        clearFilters={clearFilters}
        forceReload={forceReload}
      />
    );
  };
  return WrappedComponentEx;
}
